import React, { useCallback, useEffect } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { isIOS } from 'react-device-detect'
import { logEvent } from '@/logging'
import useBasePath from '@/hooks/useBasePath'
import { searchableUtilityProgramsCollection } from '@/reducers/utilityPrograms'
import useRemoteConfig from '@/hooks/useRemoteConfig'
import OptiwattLogo from '@/app/pages/autogrid/optiwatt-logo.webp'
import { LoadingAnimation, Text } from '@/components'
import { Link } from 'app-components'
import { motion } from 'motion/react'
import useImagesLoaded from '@/hooks/useImageLoader'
import { debounce } from 'lodash'
import useStoreLinks from '@/app/hooks/useStoreLinks/useStoreLinks'
import useExternalNavigation from '@/app/hooks/useExternalNavigation'
import useIsMobile from '@/authenticated/hooks/useIsMobile'

const MOBILE_SCHEME = 'optiwatt'
const DEFAULT_PATH = 'app'
const REDIRECT_DEPLAY_SECONDS = 5

type URLParams = {
  appPath: string | undefined
}

/**
 * @param appPath - The path of that we want to direct our deep link to. In most cases, this is
 * "navigate", meaning we want to open optiwatt://navigate?url=...
 * @param location - The current location that should contain the `url` param, along with
 * any other params passed to the deep link.
 * @returns A deeplink URL that can be used to open the mobile app with the given path and params.
 *
 * example:
 * getMobileAppUrl('navigate', { search: 'url=utility-connection/voltus/create&utility_id=42715&association_id=a819d455-dfca-4899-ac37-53b64df096fa' })
 * returns:
 * optiwatt://navigate?url=utility-connection%2Fvoltus%2Fcreate%3Futility_id%3D42715%26association_id%3Da819d455-dfca-4899-ac37-53b64df096fa
 */
export function getMobileAppUrl(
  appPath: string,
  location: ReturnType<typeof useLocation>,
) {
  const params = new URLSearchParams(location?.search)
  const route = params.get('url')
  params.delete('url')
  const remainingParams = params.toString()
  const remainingParamsUrl = remainingParams ? `?${remainingParams}` : ''
  const navUrl = `${route}${remainingParamsUrl}`
  return `${MOBILE_SCHEME}://${appPath}?url=${encodeURIComponent(navUrl)}`
}

/**
 * @param location - The current location that should contain the `url` param, along with
 * any other params passed web link
 * @returns A web app URL pointed at url with the other params appended to it
 *
 * example:
 * getWebAppUrl({ search: 'url=utility-connection/voltus/create&utility_id=42715&association_id=a819d455-dfca-4899-ac37-53b64df096fa' })
 * returns:
 * /utility-connection/voltus/create?utility_id=42715&association_id=a819d455-dfca-4899-ac37-53b64df096fa
 */
export function getWebAppUrl(location: ReturnType<typeof useLocation>) {
  const params = new URLSearchParams(location?.search)
  const route = params.get('url')
  params.delete('url')
  const remainingParams = params.toString()
  const remainingParamsUrl = remainingParams ? `?${remainingParams}` : ''
  return `/${route}${remainingParamsUrl}`
}

function DeviceTrafficControl() {
  const history = useHistory()
  const { appPath: maybeAppPath } = useParams<URLParams>()
  const location = useLocation()
  const appPath = maybeAppPath ?? DEFAULT_PATH
  const mobileAppUrl = getMobileAppUrl(appPath, location)
  const isMobile = useIsMobile()
  const appBasePath = useBasePath()
  const { appRemoteConfig, isLoading: remoteConfigLoading } = useRemoteConfig()
  const { isFinished: imagesLoaded } = useImagesLoaded([OptiwattLogo])
  const { externallyNavigate } = useExternalNavigation()

  // We can customize the mobile app download link location via CMS config
  // Create a whitelabel slug in our remote config that maps to a utility program's
  // viewConfig, and edit the view config's google/apple store URLs. Then we'll
  // send incoming deeplinkable users to those custom product pages if they don't
  // yet have the app installed
  const whiteLabelId =
    appRemoteConfig?.getWhitelabelId(appBasePath ?? '') ?? null
  const { data: utilityPrograms, isLoadingOrIdle } =
    searchableUtilityProgramsCollection.useFetch(whiteLabelId ?? -1, {
      require: Boolean(whiteLabelId) && whiteLabelId !== null,
    })
  const viewConfig = utilityPrograms?.[0]?.view_config_json
  const { androidUrl, appleUrl } = useStoreLinks(viewConfig)

  const tryToOpenInMobile = () => {
    console.log(
      'DeviceTrafficControl: navigating to mobileAppUrl',
      mobileAppUrl,
    )
    window.location.replace(mobileAppUrl)
  }

  const navigateToWebApp = () => {
    // 'navPath' refers to our deeplinking catch-all 'navigate' -- if we
    // are trying to use the /navigate route, which supports arbitrary deeplinking
    // in the native mobile app, we want to remove the navigate portion of the
    // route when navigating on web
    if (appPath.toLowerCase() === 'navigate') {
      const params = new URLSearchParams(location?.search)
      const route = params.get('url')
      fireDeeplinkEvent(route)

      const webAppUrl = getWebAppUrl(location)
      console.log('DeviceTrafficControl: navigating to webAppUrl', webAppUrl)
      history.replace(webAppUrl)
    } else {
      fireDeeplinkEvent(appPath)
      history.replace(`/${appPath}`)
    }
  }

  const fireDeeplinkEvent = (route: string | null) => {
    if (!route) {
      return
    }

    if (route.includes('connect-device/vehicle/ford')) {
      logEvent('ford_connection_qr_scan')
    }
  }

  const navigateToDeviceAppStore = useCallback(() => {
    if (isIOS) {
      externallyNavigate(appleUrl)
    } else {
      externallyNavigate(androidUrl)
    }
  }, [remoteConfigLoading])

  const isLoading =
    (whiteLabelId && isLoadingOrIdle) || remoteConfigLoading || !imagesLoaded

  const debouncedNavigation = useCallback(
    debounce(() => {
      if (isMobile && !remoteConfigLoading) {
        navigateToDeviceAppStore()
      } else {
        navigateToWebApp()
      }
    }, 1000 * REDIRECT_DEPLAY_SECONDS),
    [isMobile, remoteConfigLoading],
  )

  useEffect(() => {
    if (isLoading) {
      return
    }

    if (!isMobile) {
      navigateToWebApp()
      return
    }

    tryToOpenInMobile()

    debouncedNavigation()
  }, [isLoading, whiteLabelId, isMobile])

  if (isLoading) {
    return null
  }

  return (
    <motion.div
      className="flex gap-4 max-w-[400px] flex-col justify-center items-center h-screen px-8 mx-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div id="image-container" className="w-full h-[98px]">
        <img alt="optiwatt-logo" src={OptiwattLogo} className="w-full" />
      </div>
      <Text variant="h2" className="text-center">
        Redirecting you to the Optiwatt app.
      </Text>
      <LoadingAnimation type="falling" centered />
      <Text variant="body4" className="text-center">
        If you haven't been redirected within {REDIRECT_DEPLAY_SECONDS} seconds,{' '}
        <Link onClick={navigateToWebApp}>click here</Link>.
      </Text>
    </motion.div>
  )
}

export default DeviceTrafficControl
